import { pack } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'

/**
 * Sums all provided numbers and lists of numbers together into one total value
 *
 * @param expressions Values to sum together
 * @example
 * // Numbers
 * { '+': [ 1, 2, 3 ] }
 * // ->
 * 6
 * // Lists
 * { '+': [ [ 1, 2 ], [ 3 ] ] }
 * // ->
 * 6
 * // Numbers and lists
 * { '+': [ [ 1, 2 ], 3 ] }
 * // ->
 * 6
 */
export interface Add<out T = any> {
  /** {@link Add} */
  readonly [Key.Add]: T extends number
    ? Maybe.ReadonlyArray<Expression<number | readonly number[]>>
    : never
}
export namespace Add {
  export const isAdd: TypeChecker<Add> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Add>({
      [Key.Add]: CommonArgs.isListOfExpressions as TypeChecker<Add[Key.Add]>,
    }),
  ).usingAlias('Fn.Add')
  export const assertIsAdd: TypeAsserter<Add> = TypeAsserter.Factory.from(isAdd)

  /**
   * @see {@link Node}
   */
  export class Node<T extends number = number> extends fnNode.Node<
    T,
    Key.Add,
    readonly expressionNode.Node[]
  > {
    // NOTE: done as getter in case underlying nodes have getters
    public get type(): common.value.types.Type {
      return (
        this.arg.find(node =>
          common.value.types.Intrinsic.ForNumber.isForNumber(node.type),
        )?.type ?? common.value.types.Intrinsic.Decimal
      )
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.Add, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Add>['expressionsInArgs'] = fn =>
    pack(fn[Key.Add])
  export const HELPER: FnHelper<Add> = {
    createNode: standardNodeCreator.usingExpressionList({
      // If there is nothing to add, the sum is 0
      emptyValue: 0,
      finalizeArgs: (args, stash) => [
        ...args,
        // If the value sum is non-zero, we need to include it in the args
        ...(stash.sum === 0 ? [] : [new expressionNode.ForValue(stash.sum)]),
      ],
      getArgExpressions: expressionsInArgs,
      // We just sum up all value nodes to create one summed value expression at
      // the end
      handleValue: (node, stash) => {
        for (const val of pack(node.value)) {
          stash.sum += Number(val)
        }
      },
      nodeClass: Node,
      setupStash: () => ({ sum: 0 }),
      treatSingleArgAs: node =>
        common.value.types.Intrinsic.ForNumber.isForNumber(node.type)
          ? undefined
          : 'arg',
    }),
    expressionsInArgs,
    typeChecker: isAdd,
  }
}
